import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import useFormatters from '../lib/useFormatters';
import {
  ArrowLeft, Plus, Home, HeartPulse, Sliders, Trash2, ExternalLink, Building2,
  DollarSign, TrendingUp, Shield, Layers, Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';
import { PropertyHealthBadge } from '../components/portfolio/PropertyHealthBadge';
import AddPropertyDialog from '../components/portfolio/AddPropertyDialog';
import ActionCenter from '../components/portfolio/ActionCenter';
import NextMoveCard from '../components/portfolio/NextMoveCard';
import StressResilienceCard from '../components/portfolio/StressResilienceCard';
import PortfolioAwareAdvisor from '../components/PortfolioAwareAdvisor';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Locale-aware money/percent formatters. Read `document.documentElement.lang`
// (kept in sync by i18next) so the module-level helpers used by nested
// components stay in step with the app's current locale.
const _isFr = () => (typeof document !== 'undefined' && document.documentElement?.lang?.startsWith('fr'));
const fmtMoney = (v, { compact = false, sign = false } = {}) => {
  if (v === null || v === undefined || Number.isNaN(v)) return '—';
  const n = Number(v);
  const isFr = _isFr();
  const p = sign && n > 0 ? '+' : '';
  if (compact) {
    if (Math.abs(n) >= 1e6) return isFr ? `${p}${(n/1e6).toFixed(2).replace('.',',')} M$` : `${p}$${(n/1e6).toFixed(2)}M`;
    if (Math.abs(n) >= 1e3) return isFr ? `${p}${(n/1e3).toFixed(0)} k$` : `${p}$${(n/1e3).toFixed(0)}K`;
  }
  const abs = new Intl.NumberFormat(isFr ? 'fr-CA' : 'en-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 0 }).format(n);
  return sign && n > 0 ? `+${abs}` : abs;
};
const fmtPct = (v, d = 1) => (v === null || v === undefined || Number.isNaN(v) ? '—' : (_isFr() ? `${Number(v).toFixed(d).replace('.',',')} %` : `${Number(v).toFixed(d)}%`));

export default function MyPortfolio() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  // Keep <html lang> in sync so the module-level formatters above pick the right locale.
  useEffect(() => { if (typeof document !== 'undefined') document.documentElement.lang = i18n.language || 'en'; }, [i18n.language]);
  const [deals, setDeals] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  const load = async () => {
    try {
      const [u, d] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/deals?sort_by=cash_flow_high`, { withCredentials: true }),
      ]);
      setUser(u.data);
      setDeals(d.data);
    } catch (e) {
      if (e.response?.status === 401) navigate('/');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => {
    if (!deals.length) return null;
    const totalValue = deals.reduce((s, d) => s + (d.purchase_price || 0), 0);
    const totalEquity = deals.reduce((s, d) => s + (d.purchase_price * (d.down_payment_percent || 20) / 100), 0);
    const totalDebt = totalValue - totalEquity;
    const monthlyCf = deals.reduce((s, d) => s + (d.monthly_cash_flow || 0), 0);
    const annualCf = deals.reduce((s, d) => s + (d.annual_cash_flow || d.monthly_cash_flow * 12 || 0), 0);
    const withRoi = deals.filter((d) => d.roi);
    const wRoi = withRoi.length
      ? withRoi.reduce((s, d) => s + d.roi * d.purchase_price * (d.down_payment_percent || 20) / 100, 0) /
        (withRoi.reduce((s, d) => s + d.purchase_price * (d.down_payment_percent || 20) / 100, 0) || 1)
      : null;
    const withCap = deals.filter((d) => d.cap_rate);
    const wCap = withCap.length
      ? withCap.reduce((s, d) => s + d.cap_rate * d.purchase_price, 0) /
        (withCap.reduce((s, d) => s + d.purchase_price, 0) || 1)
      : null;
    const count = deals.length;
    return {
      totalValue, totalEquity, totalDebt,
      ltv: totalValue > 0 ? (totalDebt / totalValue) * 100 : null,
      monthlyCf, annualCf, roi: wRoi, cap: wCap,
      count,
      avgValue: totalValue / count,
      avgEquity: totalEquity / count,
      avgLtv: totalValue > 0 ? (totalDebt / totalValue) * 100 : null,
    };
  }, [deals]);

  const removeFromPortfolio = async (dealId) => {
    if (!window.confirm(t('myPortfolio.actions.confirmRemove'))) return;
    try {
      await axios.post(`${BACKEND_URL}/api/deals/${dealId}/unsave`, {}, { withCredentials: true });
      setDeals((prev) => prev.filter((d) => d.deal_id !== dealId));
      toast.success(t('myPortfolio.actions.removed'));
    } catch {
      toast.error(t('errors.saveFailed', 'Failed'));
    }
  };

  const onAdded = (deal) => {
    setDeals((prev) => [deal, ...prev]);
  };

  // Wave 1B.2A — feature-access gate uses `effective_capabilities`.
  const isPremium = (user?.effective_capabilities || []).includes('portfolio_ai') || user?.subscription_plan === 'premium';

  if (loading) {
    return (
      <Shell t={t} navigate={navigate}>
        <div className="flex items-center justify-center py-32">
          <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#45C04A]" />
        </div>
      </Shell>
    );
  }

  return (
    <Shell t={t} navigate={navigate}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#12355B] to-[#1565C0] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 data-testid="my-portfolio-title" className="font-primary font-extrabold text-3xl lg:text-4xl text-[#12355B] dark:text-white tracking-tight">
                {t('myPortfolio.pageTitle')}
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">{t('myPortfolio.pageSubtitle')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isPremium && (
              <Button
                data-testid="open-portfolio-doctor-btn"
                onClick={() => navigate('/portfolio-doctor')}
                className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white rounded-xl px-5 font-semibold"
              >
                <HeartPulse className="w-4 h-4 mr-2" />{t('portfolioDoctor.pageTitle')}
              </Button>
            )}
            <Button
              data-testid="add-property-btn"
              onClick={() => setAddOpen(true)}
              className="bg-[#45C04A] hover:bg-[#65D95A] text-[#12355B] rounded-xl px-5 font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />{t('myPortfolio.addProperty')}
            </Button>
          </div>
        </div>

        {/* Empty state */}
        {deals.length === 0 && (
          <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
              <Home className="w-8 h-8 text-slate-400" />
            </div>
            <h2 className="text-xl font-primary font-bold text-[#12355B] dark:text-white mb-2">
              {t('myPortfolio.empty.title')}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6 max-w-md mx-auto">
              {t('myPortfolio.empty.subtitle')}
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                data-testid="empty-analyze-cta"
                onClick={() => navigate('/analyze')}
                className="bg-[#12355B] hover:bg-[#1565C0] text-white rounded-xl px-6 font-semibold"
              >
                {t('myPortfolio.empty.analyzeCta')}
              </Button>
              <Button
                data-testid="empty-manual-cta"
                onClick={() => setAddOpen(true)}
                variant="outline"
                className="rounded-xl px-6 font-semibold"
              >
                {t('myPortfolio.empty.manualCta')}
              </Button>
            </div>
          </div>
        )}

        {/* KPIs */}
        {kpis && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Kpi testId="mp-total-value" label={t('portfolioDoctor.metrics.totalValue')} value={fmtMoney(kpis.totalValue, { compact: true })} icon={Building2} />
              <Kpi testId="mp-total-equity" label={t('portfolioDoctor.metrics.totalEquity')} value={fmtMoney(kpis.totalEquity, { compact: true })} icon={TrendingUp} />
              <Kpi testId="mp-total-debt" label={t('portfolioDoctor.metrics.totalDebt')} value={fmtMoney(kpis.totalDebt, { compact: true })} icon={Shield} />
              <Kpi testId="mp-ltv" label={t('portfolioDoctor.metrics.portfolioLtv')} value={fmtPct(kpis.ltv, 0)} icon={Sliders} />
              <Kpi testId="mp-monthly-cf" label={t('portfolioDoctor.metrics.monthlyCF')} value={fmtMoney(kpis.monthlyCf, { sign: true })} tone={kpis.monthlyCf >= 0 ? 'positive' : 'negative'} icon={DollarSign} />
              <Kpi testId="mp-annual-cf" label={t('portfolioDoctor.metrics.annualCF')} value={fmtMoney(kpis.annualCf, { sign: true })} tone={kpis.annualCf >= 0 ? 'positive' : 'negative'} icon={DollarSign} />
              <Kpi testId="mp-roi" label={t('portfolioDoctor.metrics.portfolioROI')} value={fmtPct(kpis.roi, 1)} icon={TrendingUp} />
              <Kpi testId="mp-cap" label={t('portfolioDoctor.metrics.portfolioCap')} value={fmtPct(kpis.cap, 1)} icon={TrendingUp} />
            </div>
            {/* Averages */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
              <Kpi testId="mp-count" label={t('myPortfolio.propertyCount')} value={kpis.count} sub={t('portfolio.properties')} />
              <Kpi testId="mp-avg-value" label={t('myPortfolio.avgValue')} value={fmtMoney(kpis.avgValue, { compact: true })} muted />
              <Kpi testId="mp-avg-equity" label={t('myPortfolio.avgEquity')} value={fmtMoney(kpis.avgEquity, { compact: true })} muted />
              <Kpi testId="mp-avg-ltv" label={t('myPortfolio.avgLtv')} value={fmtPct(kpis.avgLtv, 0)} muted />
            </div>
          </div>
        )}

        {/* RendPleX Next Move™ — Premium intelligence card (locked preview for Free/Pro) */}
        {deals.length > 0 && <NextMoveCard isPremium={isPremium} />}

        {/* Portfolio Stress Test — compact resilience card, deep-links to /stress-test */}
        {deals.length > 0 && <StressResilienceCard isPremium={isPremium} />}

        {/* Action Center */}
        {isPremium && kpis && <ActionCenter variant="full" />}

        {/* Properties */}
        {deals.length > 0 && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-[#12355B]/10 dark:bg-[#45C04A]/10">
                <Layers className="w-5 h-5 text-[#12355B] dark:text-[#45C04A]" />
              </div>
              <h2 className="font-primary font-bold text-lg text-[#12355B] dark:text-white">
                {t('myPortfolio.propertiesTitle')} · <span className="text-slate-400">{deals.length}</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
              {deals.map((d) => (
                <PropertyCard key={d.deal_id} deal={d} t={t} navigate={navigate} onRemove={removeFromPortfolio} />
              ))}
            </div>
          </div>
        )}
      </div>

      <AddPropertyDialog open={addOpen} onClose={() => setAddOpen(false)} onAdded={onAdded} />

      {/* Portfolio-Aware AI Advisor — Premium only. Backend enforces
          `ai_advisor_portfolio` capability so mounting the component on
          the client cannot leak portfolio intelligence to lower tiers. */}
      {isPremium && kpis && (
        <PortfolioAwareAdvisor
          contextType="portfolio"
          context={{
            num_properties: kpis.count,
            total_value: kpis.totalValue,
            equity: kpis.totalEquity,
            debt: kpis.totalDebt,
            ltv: kpis.ltv,
            monthly_cash_flow: kpis.monthlyCf,
            annual_cash_flow: kpis.annualCf,
            avg_cap_rate: kpis.cap,
            avg_roi: kpis.roi,
          }}
        />
      )}
    </Shell>
  );
}

function Shell({ t, navigate, children }) {
  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      <nav className="sticky top-0 z-40 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-3 flex justify-between items-center">
          <Link to="/dashboard" className="flex items-center gap-2"><RendPlexLogo size="md" /></Link>
          <div className="flex items-center gap-2">
            <DarkModeToggle />
            <LanguageSwitcher variant="minimal" />
            <Button
              data-testid="back-to-dashboard-button"
              onClick={() => navigate('/dashboard')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" /> {t('nav.backToDashboard')}
            </Button>
          </div>
        </div>
      </nav>
      {children}
      <Footer />
    </div>
  );
}

function Kpi({ testId, label, value, sub, tone = 'default', muted, icon: Icon }) {
  const tc = tone === 'positive' ? 'text-emerald-500' : tone === 'negative' ? 'text-red-500' : muted ? 'text-slate-600 dark:text-slate-300' : 'text-[#12355B] dark:text-white';
  return (
    <div data-testid={testId} className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-shadow">
      <div className="flex items-center justify-between mb-1">
        <p className="text-[10px] uppercase tracking-[0.14em] text-slate-400 font-semibold">{label}</p>
        {Icon && <Icon className="w-3.5 h-3.5 text-slate-300" />}
      </div>
      <p className={`text-2xl font-primary font-extrabold tracking-tight ${tc}`}>{value}</p>
      {sub && <p className="text-[11px] text-slate-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function PropertyCard({ deal, t, navigate, onRemove }) {
  const equity = (deal.purchase_price || 0) * ((deal.down_payment_percent || 20) / 100);
  const debt = (deal.purchase_price || 0) - equity;
  const ltv = deal.purchase_price > 0 ? (debt / deal.purchase_price) * 100 : null;
  return (
    <div
      data-testid={`portfolio-property-${deal.deal_id}`}
      className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-5 shadow-card hover:shadow-card-hover transition-all group hover:-translate-y-[1px]"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <p className="font-primary font-bold text-[#12355B] dark:text-white truncate">{deal.property_address}</p>
          <p className="text-[11px] text-slate-400 mt-0.5">{fmtMoney(deal.purchase_price, { compact: true })} · {deal.num_units} {t('portfolio.units')}</p>
          {deal.incomplete_data && (
            <button
              data-testid={`incomplete-badge-${deal.deal_id}`}
              onClick={() => navigate(`/analyze?edit=${deal.deal_id}`)}
              className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 dark:text-amber-400 bg-amber-500/10 dark:bg-amber-500/15 border border-amber-500/30 rounded-full px-2 py-0.5 hover:bg-amber-500/20 transition-colors"
              title={t('legacyDeal.banner')}
            >
              <svg viewBox="0 0 24 24" width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v3.75m0-6.5A9.25 9.25 0 1 0 21.25 12 9.25 9.25 0 0 0 12 5.25zm.05 11.25h-.1v.1h.1v-.1z"/></svg>
              {t('legacyDeal.missingValue')}
            </button>
          )}
        </div>
        <PropertyHealthBadge status={deal.health_status} size="sm" testId={`property-health-${deal.deal_id}`} />
      </div>

      <div className="grid grid-cols-3 gap-2 mb-4">
        <MiniStat label={t('portfolioDoctor.table.monthlyCF')} value={fmtMoney(deal.monthly_cash_flow, { sign: true })} tone={(deal.monthly_cash_flow || 0) >= 0 ? 'positive' : 'negative'} />
        <MiniStat label={t('portfolioDoctor.table.roi')} value={fmtPct(deal.roi, 1)} />
        <MiniStat label={t('portfolioDoctor.table.capRate')} value={fmtPct(deal.cap_rate, 1)} />
      </div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        <MiniStat label={t('portfolioDoctor.table.equity')} value={fmtMoney(equity, { compact: true })} muted />
        <MiniStat label={t('portfolioDoctor.table.debt')} value={fmtMoney(debt, { compact: true })} muted />
        <MiniStat label={t('portfolioDoctor.table.ltv')} value={fmtPct(ltv, 0)} muted />
      </div>

      <div className="flex flex-wrap gap-2">
        <Button
          data-testid={`analyze-${deal.deal_id}`}
          onClick={() => navigate(`/deal/${deal.deal_id}`)}
          size="sm"
          className="flex-1 bg-[#12355B] hover:bg-[#1565C0] text-white rounded-xl font-semibold"
        >
          <ExternalLink className="w-3.5 h-3.5 mr-1.5" />{t('myPortfolio.actions.analyze')}
        </Button>
        <Button
          data-testid={`scenario-${deal.deal_id}`}
          onClick={() => navigate(`/scenario?deal_id=${deal.deal_id}`)}
          size="sm"
          variant="outline"
          className="rounded-xl font-semibold"
        >
          <Sliders className="w-3.5 h-3.5 mr-1.5" />{t('myPortfolio.actions.runScenario')}
        </Button>
        <Button
          data-testid={`remove-${deal.deal_id}`}
          onClick={() => onRemove(deal.deal_id)}
          size="sm"
          variant="ghost"
          className="text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tone, muted }) {
  const tc = tone === 'positive' ? 'text-emerald-500' : tone === 'negative' ? 'text-red-500' : muted ? 'text-slate-600 dark:text-slate-300' : 'text-[#12355B] dark:text-white';
  return (
    <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg px-2.5 py-2 text-center">
      <p className="text-[9px] uppercase tracking-wider text-slate-400">{label}</p>
      <p className={`text-xs font-primary font-bold ${tc}`}>{value}</p>
    </div>
  );
}
